import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MaterialCategoryDTO } from '@scip/api';
import { Table } from 'primeng/table';
import { map, take } from 'rxjs/operators';

import { MaterialCategoryService } from '../../../services';
import { translationKeys } from '../../../core';
import { typeField } from '@avano/avano-components/utils';
import { TableColumnConfiguration } from '@avano/avano-components/avano-table';

@Component({
  selector: 'scip-material-selection-dialog',
  templateUrl: './material-selection-dialog.component.html',
  styleUrls: ['./material-selection-dialog.component.scss']
})
export class MaterialSelectionDialogComponent implements OnInit {
  @Input()
  set selectedCategories(val: MaterialCategoryDTO[]) {
    if (val && val.length !== 0) {
      this._selectedCategories = [...val];
    }
  }

  get selectedCategories(): MaterialCategoryDTO[] {
    return this._selectedCategories;
  }

  @Output() readonly saveEvent: EventEmitter<MaterialCategoryDTO[]> = new EventEmitter<MaterialCategoryDTO[]>();
  @Output() readonly cancelEvent: EventEmitter<void> = new EventEmitter<void>();

  cols: TableColumnConfiguration[];
  selectedCategoriesColumns: TableColumnConfiguration[];
  columnKeys = {
    text: typeField<MaterialCategoryDTO>('additionalText')
  };
  filteredMaterials: MaterialCategoryDTO[] = [];
  selectedNodeLeft: MaterialCategoryDTO;
  selectedNodeRight: MaterialCategoryDTO;
  showDialog = true;
  translationKeys: typeof translationKeys = translationKeys;

  private allMaterials: MaterialCategoryDTO[] = [];
  private _selectedCategories: MaterialCategoryDTO[] = [];

  constructor(private materialCategoryService: MaterialCategoryService, private changeDet: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.cols = [
      {
        field: this.columnKeys.text,
        header: translationKeys.materialSelectDialogComponent.html.column.categories,
        filterable: true,
        filterConfiguration: {
          filterField: this.columnKeys.text
        },
        sortable: false
      }
    ];
    this.selectedCategoriesColumns = [
      {
        field: this.columnKeys.text,
        header: translationKeys.materialSelectDialogComponent.html.column.selection
      }
    ];

    // As long as we do not show the categories as a tree (hierarchical), we just hide the not selectable parent categories
    // The hierarchically tree view should come with SCIP-956
    this.materialCategoryService
      .getAllMaterialCategories()
      .pipe(
        map((allCategories: MaterialCategoryDTO[]) => allCategories.filter((singleCategory: MaterialCategoryDTO) => singleCategory.selectable)),
        map((allSelectableCategories: MaterialCategoryDTO[]) => this.sortCategories(allSelectableCategories)),
        take(1)
      )
      .subscribe((selectableCategories: MaterialCategoryDTO[]) => {
        this.allMaterials = selectableCategories;
        this.checkSavedSelectedCategories();
        this.changeDet.markForCheck();
      });
  }

  transferSelection(): void {
    if (this.selectedNodeLeft) {
      this._selectedCategories.push(this.selectedNodeLeft);
      this.filteredMaterials.splice(this.filteredMaterials.indexOf(this.selectedNodeLeft), 1);
      this.updateAfterTransfer();
    }
  }

  removeSelectedNode(): void {
    if (this.selectedNodeRight) {
      this._selectedCategories.splice(this.selectedCategories.indexOf(this.selectedNodeRight), 1);
      this.filteredMaterials.push(this.selectedNodeRight);

      this.updateAfterTransfer();
    }
  }
  removeAll(): void {
    this._selectedCategories = [];
    this.filteredMaterials = [...this.allMaterials];
    this.updateAfterTransfer();
  }

  setAvailableLeft(nodeAvailableCategory: MaterialCategoryDTO): void {
    this.selectedNodeLeft = nodeAvailableCategory;
    this.selectedNodeRight = undefined;
  }

  setSelectedRight(selectedCategory: MaterialCategoryDTO): void {
    this.selectedNodeLeft = undefined;
    this.selectedNodeRight = selectedCategory;
  }

  // Filter out the already added categories from the selectable table on the left side
  private checkSavedSelectedCategories(): void {
    if (this._selectedCategories) {
      this.filteredMaterials = this.allMaterials.filter((cat) => {
        return !this._selectedCategories.some((selectedCategory) => cat.identifier === selectedCategory.identifier);
      });
    } else {
      this._selectedCategories = [];
      this.filteredMaterials = [...this.allMaterials];
    }
  }

  private updateAfterTransfer(): void {
    // No matter in which direction we transfer something we discard the selection on both sides
    this.selectedNodeRight = undefined;
    this.selectedNodeLeft = undefined;
    this.filteredMaterials = this.sortCategories(this.filteredMaterials);
    this._selectedCategories = this.sortCategories(this._selectedCategories);
  }

  private sortCategories(unsortedCategories: MaterialCategoryDTO[]): MaterialCategoryDTO[] {
    // Sort mutates the array, because of primeNG we need a new one, therefore spread it ;)
    return [...unsortedCategories.sort((a, b) => (a.additionalText > b.additionalText ? 1 : b.additionalText > a.additionalText ? -1 : 0))];
  }
}
